import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkStatusInvoice, uploadPayment, getAllPaymentByUserId } from '../../../../redux/actions/payment';
import { updateProfileUser } from '../../../../redux/actions/users';
import { toast } from 'react-hot-toast';
import { InputSwitch } from '../../../../components/inputSwitch/InputSwitch';
import { ImageUploadDropzone } from '../../../../components/imageUpload/ImageUploadDropzone';

export const TabPaymentUpload = ({ userId, isOpen, onRegisterFooterActions, onUploadSuccess, onOpenProfileDrawer }) => {
    const dispatch = useDispatch();
    const { paymentsByUser } = useSelector((state) => state.payment);
    const [activateInvoice, setActivateInvoice] = useState(false);
    const [checkActivateInvoice, setCheckActivateInvoice] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updatingInvoice, setUpdatingInvoice] = useState(false);
    const [loadingInvoiceStatus, setLoadingInvoiceStatus] = useState(false);

    // Obtener la URL de la imagen existente (del pago pendiente o rechazado)
    const pendingPayment = paymentsByUser.find(payment => 
        payment.status === 'pendiente' || payment.status === 'rechazado'
    );
    const existingImageUrl = pendingPayment?.urlName || null;

    // Verificar status de invoice cada vez que se abre el drawer
    useEffect(() => {
        if (isOpen && userId) {
            setLoadingInvoiceStatus(true);
            dispatch(checkStatusInvoice(userId))
                .then((result) => {
                    if (result.status === 200) {
                        setCheckActivateInvoice(result.data.invoiceStatus);
                        setActivateInvoice(result.data.invoiceActive || false);
                    }
                })
                .finally(() => {
                    setLoadingInvoiceStatus(false);
                });
        }
    }, [isOpen, userId, dispatch]);

    // Resetear formulario cuando se cierra el drawer
    useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setLoading(false);
        }
    }, [isOpen]);

    const handleFileChange = (file) => {
        setSelectedFile(file);
    };

    const handleFileRemove = () => {
        setSelectedFile(null);
    };

    const handleToggleInvoice = async (newValue) => {
        // Si checkActivateInvoice es true, no permitir cambios
        if (checkActivateInvoice) {
            return;
        }

        setUpdatingInvoice(true);
        try {
            const result = await dispatch(updateProfileUser({ invoiceActive: newValue }, userId));
            if (result.status === 201) {
                setActivateInvoice(newValue);
                toast.success(
                    newValue 
                        ? 'Facturación automática activada correctamente' 
                        : 'Facturación automática desactivada correctamente'
                );
            } else {
                toast.error('Error al actualizar el estado de facturación');
            }
        } catch (error) {
            toast.error('Error al actualizar el estado de facturación');
        } finally {
            setUpdatingInvoice(false);
        }
    };

    const handleSubmitUploadPayment = useCallback(() => {
        if (!selectedFile) {
            toast.error('Seleccione su comprobante de pago.');
            return;
        }
        setLoading(true);
        const payload = {
            userId,
            invoiceActive: activateInvoice,
            file: selectedFile,
        };
        dispatch(uploadPayment(payload))
            .then((result) => {
                if (result.status === 201) {
                    toast.success(result.message);
                    setSelectedFile(null);
                    setLoading(false);
                    dispatch(getAllPaymentByUserId(userId));
                    // Cambiar al tab de historial después de subir exitosamente
                    if (onUploadSuccess) {
                        setTimeout(() => onUploadSuccess(), 500); // Pequeño delay para que se vea el toast
                    }
                } else {
                    toast.error('Error al subir el comprobante de pago');
                    setLoading(false);
                }
            })
            .catch(() => {
                toast.error('Error al subir el comprobante de pago');
                setLoading(false);
            });
    }, [userId, activateInvoice, selectedFile, dispatch, onUploadSuccess]);

    // Registrar acciones del footer (botón Subir Comprobante) cuando este tab está visible
    useEffect(() => {
        if (!onRegisterFooterActions) return;
        onRegisterFooterActions({
            loading,
            disabled: loading || !selectedFile,
            onSubmit: handleSubmitUploadPayment,
        });
        // No limpiar al desmontar para evitar re-renders innecesarios
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, selectedFile]);

    return (
        <div className='h-full flex flex-col'>
            {/* Header fijo */}
            <div className="flex-shrink-0 mb-6">
                <div className="bg-white border border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Subir comprobante de pago
                                </h3>
                                <p className="text-xs text-gray-500 font-medium mt-0.5 hidden lg:block">
                                    Subir tu comprobante de pago para validar tu pago.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenido con scroll */}
            <div className="flex-1 overflow-y-auto pr-2">
                <div className='flex w-full flex-col items-center border-0 md:border-[1px] border-gray-200 rounded-lg px-5 py-2 lg:py-4 shadow-sm mb-6'>
                    <div className='w-full grid grid-cols-1 gap-4'>
                        <ImageUploadDropzone
                            file={selectedFile}
                            previewUrl={existingImageUrl}
                            onFileChange={handleFileChange}
                            onFileRemove={handleFileRemove}
                            disabled={loading || loadingInvoiceStatus}
                        />

                        <div className='w-full flex flex-col align-middle ring-1 ring-indigo-900/10 rounded-sm px-4 pt-2 pb-6 shadow-sm'>
                            {loadingInvoiceStatus ? (
                                // Skeleton loading para la sección de facturación
                                <div className='animate-pulse'>
                                    <div className='flex items-center justify-between mb-4'>
                                        <div className='h-5 bg-gray-200 rounded w-64'></div>
                                        <div className='h-6 w-12 bg-gray-200 rounded-full'></div>
                                    </div>
                                    <div className='space-y-2'>
                                        <div className='h-4 bg-gray-200 rounded w-full'></div>
                                        <div className='h-4 bg-gray-200 rounded w-3/4'></div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <InputSwitch
                                        enabled={activateInvoice}
                                        setEnabled={handleToggleInvoice}
                                        label={activateInvoice ? "Desactivar Facturación Automática" : "Activar Facturación Automática"}
                                        disabled={checkActivateInvoice || updatingInvoice}
                                    />
                                    {checkActivateInvoice && (
                                        <p className='text-sm text-gray-900 tracking-normal mt-2'>
                                            Si deseas activar la facturación automática, debes registrar tus datos fiscales en tu{' '}
                                            <button
                                                type='button'
                                                onClick={onOpenProfileDrawer}
                                                className='font-bold text-md text-indigo-600 hover:text-indigo-800 underline transition-colors'
                                            >
                                                Perfil
                                            </button>
                                        </p>
                                    )}
                                    {activateInvoice ? (
                                        <p className='text-sm text-gray-900 tracking-normal mt-2'>
                                            Desactivar facturación automática. Esto cancelará el envío de facturas al correo electrónico.
                                        </p>
                                    ) : !checkActivateInvoice ? (
                                        <p className='text-sm text-gray-900 tracking-normal mt-2'>
                                            Activar facturación automática. Esto permitirá que se envíen las facturas al correo electrónico.
                                        </p>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
